import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { decamelizeKeysDeep } from '@/utils/decamelize';

import { DEFAULT_EVENT_STYLE, EVENT_TYPE_STYLES, FALLBACK_MATCH_STEP, TONE_CLASS } from './AgentEventItem.constants';
import type { AgentEventItemProps } from './AgentEventItem.types';

const formatEventTime = (iso: string): string => {
	const d = new Date(iso);

	if (Number.isNaN(d.getTime())) return '—';
	return d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const formatDuration = (ms: number): string => (ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${ms} ms`);

export const AgentEventItem = ({ event, defaultOpen = false }: AgentEventItemProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'conversationDetail' });
	const { icon: Icon, tone } = EVENT_TYPE_STYLES[event.eventType] ?? DEFAULT_EVENT_STYLE;
	// El cliente API camelliza las keys dinámicas del payload; restauramos los
	// identificadores snake_case originales para display y lógica.
	const payload = decamelizeKeysDeep(event.payload) as Record<string, unknown>;
	const usedFallback = payload.match_step === FALLBACK_MATCH_STEP;
	const toneClass = usedFallback ? TONE_CLASS.warning : TONE_CLASS[tone];
	const hasPayload = Object.keys(payload).length > 0;

	return (
		<details
			className="group overflow-hidden rounded-md border border-subtle bg-grey-25/60"
			open={defaultOpen}
			data-testid={`agent-event-${event.id}`}
		>
			<summary className="flex cursor-pointer select-none items-center gap-2xs px-xs py-2xs transition-colors hover:bg-white [&::-webkit-details-marker]:hidden">
				<ChevronRight className="h-sm w-sm shrink-0 text-grey-200 transition-transform group-open:rotate-90" />
				<span className={`inline-flex h-lg w-lg shrink-0 items-center justify-center rounded ${toneClass}`}>
					<Icon className="h-[12px] w-[12px]" />
				</span>
				<span className="min-w-0 flex-1 truncate text-[12px] leading-tight text-subtle">
					{event.summary ?? event.eventType}
				</span>
				{event.durationMs != null && (
					<span className="shrink-0 font-mono text-[11px] leading-none text-subtle tabular-nums">
						{formatDuration(event.durationMs)}
					</span>
				)}
				<span className="shrink-0 font-mono text-[10px] leading-none text-grey-200">{event.eventType}</span>
				<span className="shrink-0 font-mono text-[11px] leading-none text-subtle tabular-nums">
					{formatEventTime(event.occurredAt)}
				</span>
			</summary>
			{hasPayload && (
				<div className="border-t border-subtle bg-white px-xs py-xs">
					<p className="mb-3xs text-[10px] font-medium uppercase tracking-wider text-subtle">{t('EVENT_PAYLOAD')}</p>
					<pre className="overflow-x-auto whitespace-pre-wrap rounded-md bg-grey-25 px-xs py-xs font-mono text-[11px] text-default">
						{JSON.stringify(payload, null, 2)}
					</pre>
				</div>
			)}
		</details>
	);
};
